import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'

// Import components with proper error boundaries
import { AuthProvider } from './components/providers/AuthProvider'
import { ThemeProvider } from './components/providers/ThemeProvider'
import { Layout } from './components/layout/Layout'
import { AgentContextLayout } from './components/layout/AgentContextLayout'
import { LandingPage } from './pages/Landing/LandingPage'
import { LoginPage } from './pages/Auth/LoginPage'
import WidgetDemo from './pages/WidgetDemo'
import { RegisterPage } from './pages/Auth/RegisterPage'
import OAuthSuccess from './pages/Auth/OAuthSuccess'
import OAuthError from './pages/Auth/OAuthError'
import { ForgotPasswordPage } from './pages/Auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/Auth/ResetPasswordPage'
import { EmailVerificationPage } from './pages/Auth/EmailVerificationPage'
import { DashboardOverview } from './pages/Dashboard/Overview'
import { ChatbotsPage } from './pages/Dashboard/Chatbots'
import { CreateAgentPage } from './pages/Dashboard/CreateAgentPage'
import { AnalyticsPage } from './pages/Dashboard/Analytics'
import RedisMonitoring from './pages/Dashboard/RedisMonitoring'
import { SettingsPage } from './pages/Dashboard/Settings'
import { PlanUpgrade } from './pages/Dashboard/PlanUpgrade'
import { ChatInterface } from './pages/Chat/ChatInterface'
import { Agents } from './pages/Dashboard/Agents'
// Docs pages are opened externally; no in-dashboard docs routes
import ApiKeysPage from './pages/Developers/ApiKeys'
import ApiGuidePage from './pages/Developers/ApiGuide'
import ApiLogs from './pages/Developers/ApiLogs'
import ChangelogPage from './pages/Dashboard/Changelog'
import { AgentDashboard } from './pages/Agent/AgentDashboard'
import { AgentLogin } from './pages/Agent/AgentLogin'
import { AgentInvitationAcceptance } from './pages/Agent/AgentInvitationAcceptance'
import { AgentConversations } from './pages/Agent/AgentConversations'
import { AgentLayout } from './components/layout/AgentLayout'
import Teams from './pages/Dashboard/Teams'
import ConversationManagement from './pages/Dashboard/ConversationManagement'
import Leads from './pages/Dashboard/Leads'
import Integrations from './pages/Dashboard/Integrations'
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { RoleProtectedRoute } from './components/layout/RoleProtectedRoute'
import { AgentRedirect } from './components/layout/AgentRedirect'
import { useAuth } from './hooks/useAuth'
import { clearExpiredTokens } from './utils/tokenUtils'
import { ToastProvider, useToast } from './hooks/useToast'
import { Toaster } from './components/ui/sonner'
import { LIMIT_EVENT } from './utils/limitEvents'

// Agent-level pages (two-level navigation)
import {
  ActivityPage,
  AgentAnalyticsPage,
  DataSourcesPage,
  ContactsPage,
  DeployPage,
  AgentSettingsPage,
  WidgetSettingsPage,
  WebhooksPage,
  FullPageChatSettingsPage,
  SuggestionsPage,
} from './pages/Dashboard/AgentPages'

// Admin components
import AdminLogin from './pages/Admin/Login/AdminLogin'
import AdminProtectedRoute from './components/admin/AdminProtectedRoute'
import AdminLayout from './components/admin/AdminLayout'
import AdminUsers from './pages/Admin/Users/AdminUsers'
import { PlanManagement } from './pages/Admin/PlanManagement'
import AdminAnalytics from './pages/Admin/Analytics/AdminAnalytics'
import AdminLlmAnalytics from './pages/Admin/Analytics/AdminLlmAnalytics'
import { UserUsageDetails } from './pages/Admin/UserUsageDetails'
import { UsageMonitoring } from './pages/Admin/UsageMonitoring'
import { LLMPricing } from './pages/Admin/LLMPricing'
import { RateLimiting } from './pages/Admin/RateLimiting'
import { CreditAnalytics } from './pages/Admin/CreditAnalytics'
import QdrantInspector from './pages/Admin/QdrantInspector'
import AdminSettings from './pages/Admin/Settings/AdminSettings'
import { AdminAuthProvider } from './hooks/useAdminAuth'

// Lazy-loaded legal pages
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService').then(m => ({ default: m.TermsOfService })))
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))

/**
 * App Routes Component
 *
 * Two-level navigation:
 *   - Org-level: /dashboard/* with Layout (main sidebar)
 *   - Agent-level: /dashboard/chatbots/:chatbotId/* with AgentContextLayout (agent sidebar)
 */
function AppRoutes() {
  const { isAuthenticated, user } = useAuth()
  const { toast } = useToast()

  // Helper function to determine correct redirect based on user type
  const getAuthenticatedRedirect = () => {
    const userType = localStorage.getItem('user_type')
    if (userType === 'agent') return '/agent/dashboard'
    return '/dashboard'
  }

  return (
    <Router>
      {(() => {
        function LimitListener() {
          useEffect(() => {
            function handler(e: any) {
              const d = e?.detail || {}
              const metric = String(d.metric || 'usage').replace('_', ' ')
              const used = d.used ?? '\u2014'
              const limit = d.limit === -1 ? 'Unlimited' : (d.limit ?? '\u2014')
              toast({
                title: 'Limit reached',
                description: `You have reached your ${metric} limit (${used}/${limit}). Upgrade your plan to continue.`,
                variant: 'warning',
                duration: 7000
              })
            }
            window.addEventListener(LIMIT_EVENT, handler as any)
            return () => window.removeEventListener(LIMIT_EVENT, handler as any)
          }, [])
          return null
        }
        return <LimitListener />
      })()}
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><TermsOfService /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<div className="min-h-screen bg-background" />}><PrivacyPolicy /></Suspense>} />
        <Route
          path="/auth/login"
          element={isAuthenticated ? <Navigate to={getAuthenticatedRedirect()} replace /> : <LoginPage />}
        />
        <Route
          path="/auth/register"
          element={isAuthenticated ? <Navigate to={getAuthenticatedRedirect()} replace /> : <RegisterPage />}
        />

        {/* Auth utility pages */}
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/verify-email" element={<EmailVerificationPage />} />

        {/* OAuth callback routes */}
        <Route path="/auth/oauth-success" element={<OAuthSuccess />} />
        <Route path="/auth/oauth-error" element={<OAuthError />} />

        {/* Public widget demo */}
        <Route path="/widget-demo" element={<WidgetDemo />} />

        {/* Agent routes */}
        <Route path="/agent/login" element={<AgentLogin />} />
        <Route path="/agent/accept-invitation" element={<AgentInvitationAcceptance />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/*" element={
          <AdminAuthProvider>
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          </AdminAuthProvider>
        }>
          <Route path="dashboard" element={<div className="p-8 text-center text-gray-700">Admin Dashboard - Coming Soon</div>} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:userId/usage" element={<UserUsageDetails />} />
          <Route path="organizations" element={<div className="p-8 text-center text-gray-700">Organizations - Coming Soon</div>} />
          <Route path="plans" element={<PlanManagement />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="analytics/llm" element={<AdminLlmAnalytics />} />
          <Route path="llm-pricing" element={<LLMPricing />} />
          <Route path="usage-monitoring" element={<UsageMonitoring />} />
          <Route path="redis-monitoring" element={<RedisMonitoring />} />
          <Route path="rate-limiting" element={<RateLimiting />} />
          <Route path="credit-analytics" element={<CreditAnalytics />} />
          <Route path="qdrant" element={<QdrantInspector />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* Agent-only protected routes */}
        <Route path="/agent" element={<RoleProtectedRoute allowedRoles={['agent']} />}>
          <Route element={<AgentLayout />}>
            <Route path="dashboard" element={<AgentDashboard />} />
            <Route path="conversations" element={<AgentConversations />} />
          </Route>
        </Route>

        {/* Company owner protected routes */}
        <Route path="/dashboard" element={<RoleProtectedRoute allowedRoles={['company_owner']} />}>
          {/* Org-level layout (main sidebar) */}
          <Route element={<AgentRedirect><Layout key={isAuthenticated ? user?.id : 'logged-out'} /></AgentRedirect>}>
            <Route index element={<DashboardOverview />} />
            <Route path="chatbots" element={<ChatbotsPage />} />
            <Route path="training" element={<Navigate to="/dashboard/chatbots" replace />} />
            <Route path="widget" element={<Navigate to="/dashboard/chatbots" replace />} />
            <Route path="agents" element={<Agents />} />
            <Route path="agents/new" element={<CreateAgentPage />} />
            <Route path="teams" element={<Teams />} />
            <Route path="tools" element={<Navigate to="/dashboard/chatbots" replace />} />
            <Route path="skills" element={<Navigate to="/dashboard/chatbots" replace />} />
            <Route path="leads" element={<Leads />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="conversations" element={<ConversationManagement />} />
            <Route path="agent-dashboard" element={<Navigate to="/agent/dashboard" replace />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="redis-monitoring" element={<RedisMonitoring />} />
            <Route path="plans" element={<PlanUpgrade />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="developers/api-keys" element={<ApiKeysPage />} />
            <Route path="developers/guide" element={<ApiGuidePage />} />
            <Route path="developers/api-logs" element={<ApiLogs />} />
            <Route path="changelog" element={<ChangelogPage />} />
          </Route>

          {/* Agent-level layout (agent sidebar) — two-level navigation */}
          <Route path="chatbots/:chatbotId" element={<AgentContextLayout />}>
            <Route index element={<AgentSettingsPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="analytics" element={<AgentAnalyticsPage />} />
            <Route path="data-sources" element={<DataSourcesPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="deploy" element={<DeployPage />} />
            <Route path="deploy/widget" element={<WidgetSettingsPage />} />
            <Route path="deploy/fullpage-chat" element={<FullPageChatSettingsPage />} />
            <Route path="webhooks" element={<WebhooksPage />} />
            <Route path="suggestions" element={<SuggestionsPage />} />
          </Route>
        </Route>

        <Route path="/chat/:chatbotId" element={<ProtectedRoute />}>
          <Route index element={<ChatInterface />} />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}

/**
 * Main App Component
 */
function App() {
  // Clean up expired tokens on app load
  useEffect(() => {
    clearExpiredTokens()
  }, [])

  return (
    <ThemeProvider defaultTheme="dark" storageKey="bgai-theme">
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
          <Toaster />
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
